import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, Download, Calendar, TrendingUp, Package, 
  DollarSign, Users, BarChart3, ShoppingBag
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseReports = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('enterprise_token');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    fetchStats();
  }, [token, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'HTG') => {
    return new Intl.NumberFormat('fr-HT').format(amount) + ' ' + currency;
  };

  const reportTypes = [
    {
      title: "Rapò Kòmand",
      description: "Tout kòmand pa peryòd",
      icon: Package,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: "Rapò Depans",
      description: "Depans pa depatman ak kategori",
      icon: DollarSign,
      color: "bg-green-100 text-green-600"
    },
    {
      title: "Rapò Itilizatè",
      description: "Aktivite itilizatè yo",
      icon: Users,
      color: "bg-purple-100 text-purple-600"
    },
    {
      title: "Rapò Bidjè",
      description: "Eta bidjè ak previzyon",
      icon: TrendingUp,
      color: "bg-orange-100 text-orange-600"
    },
    {
      title: "Rapò Marketplace",
      description: "Vant, komisyon ak revni machann",
      icon: ShoppingBag,
      color: "bg-amber-100 text-amber-600",
      isMarketplace: true
    }
  ];

  const handleExport = (reportType) => {
    toast.info(`Ekspòtasyon ${reportType} ap vini byento`);
  };

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-reports">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#232F3E]">Rapò</h1>
          <p className="text-gray-500">Analize pèfòmans ak depans antrepriz la</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Kòmand</p>
                      <p className="text-3xl font-bold text-[#232F3E]">{stats?.orders?.total || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Apwouve</p>
                      <p className="text-3xl font-bold text-green-600">{stats?.orders?.approved || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Depans USD</p>
                      <p className="text-3xl font-bold text-[#FF9900]">
                        ${(stats?.spending?.total_usd || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Itilizatè</p>
                      <p className="text-3xl font-bold text-purple-600">{stats?.users || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Marketplace Stats (Super Admin only) */}
            {stats?.marketplace_stats && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-[#232F3E] mt-8 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                  Pèfòmans Marketplace
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="border-0 shadow-lg bg-white">
                    <CardContent className="p-6">
                      <p className="text-sm text-gray-500 mb-1">Vant Total (HTG)</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(stats.marketplace_stats.total_revenue || 0)}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-2 uppercase font-black tracking-widest">
                        {stats.marketplace_stats.order_count || 0} Kòmand Konplete
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 shadow-lg bg-emerald-50">
                    <CardContent className="p-6">
                      <p className="text-sm text-emerald-600 mb-1">Peman Machann (HTG)</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        {formatCurrency(stats.marketplace_stats.seller_revenue || 0)}
                      </p>
                      <p className="text-[10px] text-emerald-400 mt-2 uppercase font-black tracking-widest">
                        Lajan ki ale jwenn machann yo
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg bg-amber-50">
                    <CardContent className="p-6">
                      <p className="text-sm text-amber-600 mb-1">Revni Sistèm (HTG)</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {formatCurrency(stats.marketplace_stats.system_revenue || 0)}
                      </p>
                      <p className="text-[10px] text-amber-400 mt-2 uppercase font-black tracking-widest">
                        8% frè sistèm nèt
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Report Types */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportTypes.map((report, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${report.color}`}>
                          <report.icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#232F3E] mb-1">{report.title}</h3>
                          <p className="text-sm text-gray-500">{report.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport(report.title)}
                        className="shrink-0"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Ekspòte
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Spending Chart Placeholder */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#FF9900]" />
                  Depans pa Mwa
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl">
                  <div className="text-center text-gray-400">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4" />
                    <p>Grafik depans ap vini byento</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </EnterpriseLayout>
  );
};

